import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Button } from '@/components/ui/Button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card'
import { Input } from '@/components/ui/Input'
import { ArrowLeft, Shield, Save, Send, CheckCircle, XCircle, Copy } from 'lucide-react'
import { useToast } from '@/contexts/ToastContext'
import {
  getFirewallChangeRequestDetail,
  updateFirewallChangeRequest,
  submitFirewallChangeRequest,
  approveFirewallChangeRequest,
  rejectFirewallChangeRequest,
  getFirewallSubmissionToken
} from '@/lib/firewall'
import { FirewallChangeRequestRow } from '@/types'
import { supabase } from '@/lib/supabase'
import { Ship } from '@/types'
import { useTranslation } from 'react-i18next'

export const FirewallDetailPage: React.FC = () => {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { success, error } = useToast()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [firewallChangeRequest, setFirewallChangeRequest] = useState<FirewallChangeRequestRow | null>(null)
  const [ship, setShip] = useState<Ship | null>(null)
  const [formData, setFormData] = useState<Partial<FirewallChangeRequestRow>>({})
  const [submissionUrl, setSubmissionUrl] = useState<string | null>(null)

  useEffect(() => {
    if (id) {
      loadDetail()
      loadSubmissionToken()
    }
  }, [id])

  const loadDetail = async () => {
    if (!id) return
    
    setLoading(true)
    try {
      const request = await getFirewallChangeRequestDetail(id)
      
      if (!request) {
        error('Error', 'Firewall change request not found')
        navigate('/firewall')
        return
      }

      setFirewallChangeRequest(request)
      setFormData(request)

      // fetch ship data
      const { data: shipData } = await supabase
        .from('ships')
        .select('*')
        .eq('id', request.ship_id)
        .single()
      
      setShip(shipData || null)
    } catch (err: any) {
      console.error('Error loading firewall change request detail:', err)
      error('Error', 'Failed to load firewall change request')
    } finally {
      setLoading(false)
    }
  }

  const loadSubmissionToken = async () => {
    if (!id) return
    
    try {
      const result = await getFirewallSubmissionToken(id)
      if (result.success && result.url) {
        setSubmissionUrl(result.url)
      }
    } catch (err) {
      console.error('Error loading submission token:', err)
    }
  }

  const handleSave = async () => {
    if (!id || !firewallChangeRequest) return

    setSaving(true)
    try {
      const result = await updateFirewallChangeRequest(id, formData)
      if (result.success) {
        success('Success', 'Firewall change request updated successfully')
        await loadDetail()
      } else {
        error('Error', result.error || 'Failed to update firewall change request')
      }
    } catch (err: any) {
      error('Error', 'Failed to save firewall change request')
    } finally {
      setSaving(false)
    }
  }

  const handleSubmitForReview = async () => {
    if (!id) return

    // validate that vendor comment is provided if needs_vendor_review is true
    if (formData.needs_vendor_review && !formData.vendor_comment?.trim()) {
      error('Error', 'Please provide a vendor comment when marking as needs vendor review')
      return
    }

    setSaving(true)
    try {
      // first, save the vendor comment and needs_vendor_review flag
      if (formData.vendor_comment !== undefined || formData.needs_vendor_review !== undefined) {
        await updateFirewallChangeRequest(id, {
          vendor_comment: formData.vendor_comment || null,
          needs_vendor_review: formData.needs_vendor_review || false
        })
      }

      // then submit for review
      const result = await submitFirewallChangeRequest(id)
      if (result.success) {
        // get the submission token url
        const tokenResult = await getFirewallSubmissionToken(id)
        if (tokenResult.success && tokenResult.url) {
          // show the url in a prompt for copying
          const userConfirmed = confirm(
            `Firewall change request submitted successfully!\n\n` +
            `Vendor submission URL:\n${tokenResult.url}\n\n` +
            `Click OK to copy the URL to clipboard, or Cancel to dismiss.`
          )
          
          if (userConfirmed) {
            navigator.clipboard.writeText(tokenResult.url)
            success('Success', 'URL copied to clipboard!')
          } else {
            success('Success', result.message)
          }
        } else {
          success('Success', result.message)
        }
        await loadDetail()
        await loadSubmissionToken()
      } else {
        error('Error', result.message)
      }
    } catch (err: any) {
      error('Error', 'Failed to submit firewall change request')
    } finally {
      setSaving(false)
    }
  }

  const handleApprove = async () => {
    if (!id) return

    setSaving(true)
    try {
      const result = await approveFirewallChangeRequest(id)
      if (result.success) {
        success('Success', result.message)
        await loadDetail()
      } else {
        error('Error', result.message)
      }
    } catch (err: any) {
      error('Error', 'Failed to approve firewall change request')
    } finally {
      setSaving(false)
    }
  }

  const handleReject = async () => {
    if (!id) return

    const reason = prompt('Enter rejection reason:')
    if (!reason) return

    setSaving(true)
    try {
      const result = await rejectFirewallChangeRequest(id, reason)
      if (result.success) {
        success('Success', result.message)
        await loadDetail()
      } else {
        error('Error', result.message)
      }
    } catch (err: any) {
      error('Error', 'Failed to reject firewall change request')
    } finally {
      setSaving(false)
    }
  }


  const copySubmissionUrl = () => {
    if (submissionUrl) {
      navigator.clipboard.writeText(submissionUrl)
      success('Success', 'Submission URL copied to clipboard')
    }
  }

  const getStatusBadgeColor = (status: FirewallChangeRequestRow['status']) => {
    switch (status) {
      case 'draft':
        return 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300'
      case 'under_review':
        return 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300'
      case 'approved':
        return 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
      case 'implemented':
        return 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
      case 'closed':
        return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
      default:
        return 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300'
    }
  }

  const getApprovalStatusBadgeColor = (approvalStatus: FirewallChangeRequestRow['approval_status']) => {
    switch (approvalStatus) {
      case 'review':
        return 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300'
      case 'under_progress':
        return 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
      case 'approved':
        return 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
      case 'rejected':
        return 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300'
      default:
        return 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300'
    }
  }

  const isReadOnly = firewallChangeRequest?.status === 'closed'

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-slate-600 dark:text-slate-400">{t('firewall.loading')}</p>
        </div>
      </div>
    )
  }

  if (!firewallChangeRequest) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <p className="text-slate-600 dark:text-slate-400">{t('firewall.empty')}</p>
          <Button onClick={() => navigate('/firewall')} className="mt-4">
            Back to Overview
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 text-slate-900 dark:text-white">
      {/* header */}
      <div className="border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">
        <div className="px-6 py-4">
          <div className="flex items-center gap-4 mb-4">
            <Button variant="secondary" size="sm" onClick={() => navigate(-1)}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div className="flex-1">
              <h1 className="text-xl font-semibold flex items-center gap-2">
                <Shield className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                Firewall Change Request Detail
              </h1>
            </div>
          </div>
          <div className="flex items-center gap-4 flex-wrap">
            <div>
              <span className="text-sm text-slate-500 dark:text-slate-400">{t('moc.requestNo')}:</span>
              <span className="ml-2 font-medium">{firewallChangeRequest.request_no || '-'}</span>
            </div>
            <div>
              <span className="text-sm text-slate-500 dark:text-slate-400">{t('moc.vessel')}:</span>
              <span className="ml-2 font-medium">{ship?.vessel_name || 'Unknown'}</span>
            </div>
            <div>
              <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusBadgeColor(firewallChangeRequest.status)}`}>
                {firewallChangeRequest.status.replace('_', ' ')}
              </span>
            </div>
            <div>
              <span className={`px-2 py-1 rounded text-xs font-medium ${getApprovalStatusBadgeColor(firewallChangeRequest.approval_status)}`}>
                {firewallChangeRequest.approval_status.replace('_', ' ')}
              </span>
            </div>
            {submissionUrl && (
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={copySubmissionUrl}>
                  <Copy className="h-4 w-4 mr-2" />
                  Copy Submission URL
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8 max-w-7xl space-y-6">
        {/* basic info section */}
        <Card>
          <CardHeader>
            <CardTitle>{t('firewall.information')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  Request Date
                </label>
                <Input
                  type="datetime-local"
                  value={formData.request_date ? new Date(formData.request_date).toISOString().slice(0, 16) : ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, request_date: e.target.value ? new Date(e.target.value).toISOString() : null }))}
                  disabled={isReadOnly}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  Requester Department
                </label>
                <Input
                  value={formData.requester_department || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, requester_department: e.target.value }))}
                  placeholder={t('firewall.department')}
                  disabled={isReadOnly}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  Requester Position
                </label>
                <Input
                  value={formData.requester_position || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, requester_position: e.target.value }))}
                  placeholder={t('firewall.position')}
                  disabled={isReadOnly}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  Requester Name
                </label>
                <Input
                  value={formData.requester_name || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, requester_name: e.target.value }))}
                  placeholder={t('firewall.name')}
                  disabled={isReadOnly}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  Installed Security Zone
                </label>
                <Input
                  value={formData.installed_security_zone || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, installed_security_zone: e.target.value }))}
                  placeholder={t('firewall.securityZone')}
                  disabled={isReadOnly}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  Installed Component
                </label>
                <Input
                  value={formData.installed_component || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, installed_component: e.target.value }))}
                  placeholder={t('fields.component')}
                  disabled={isReadOnly}
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  Reason for Change
                </label>
                <textarea
                  value={formData.reason || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, reason: e.target.value }))}
                  placeholder={t('firewall.reason')}
                  disabled={isReadOnly}
                  className="flex min-h-[80px] w-full rounded-md border border-input bg-white text-slate-900 px-3 py-2 text-sm ring-offset-background placeholder:text-slate-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* before change section */}
        <Card>
          <CardHeader>
            <CardTitle>{t('firewall.beforeChange')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  Vendor/Model
                </label>
                <Input
                  value={formData.before_vendor_model || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, before_vendor_model: e.target.value }))}
                  placeholder={t('firewall.vendorModel')}
                  disabled={isReadOnly}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  Policy Purpose
                </label>
                <Input
                  value={formData.before_policy_purpose || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, before_policy_purpose: e.target.value }))}
                  placeholder={t('firewall.policyPurpose')}
                  disabled={isReadOnly}
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  Policy Detail
                </label>
                <textarea
                  value={formData.before_policy_detail || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, before_policy_detail: e.target.value }))}
                  placeholder={t('firewall.policyDetail')}
                  disabled={isReadOnly}
                  className="flex min-h-[80px] w-full rounded-md border border-input bg-white text-slate-900 px-3 py-2 text-sm ring-offset-background placeholder:text-slate-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* after change section */}
        <Card>
          <CardHeader>
            <CardTitle>{t('firewall.afterChange')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  Vendor/Model
                </label>
                <Input
                  value={formData.after_vendor_model || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, after_vendor_model: e.target.value }))}
                  placeholder={t('firewall.vendorModel')}
                  disabled={isReadOnly}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  Policy Purpose
                </label>
                <Input
                  value={formData.after_policy_purpose || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, after_policy_purpose: e.target.value }))}
                  placeholder={t('firewall.policyPurpose')}
                  disabled={isReadOnly}
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  Policy Detail
                </label>
                <textarea
                  value={formData.after_policy_detail || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, after_policy_detail: e.target.value }))}
                  placeholder={t('firewall.policyDetail')}
                  disabled={isReadOnly}
                  className="flex min-h-[80px] w-full rounded-md border border-input bg-white text-slate-900 px-3 py-2 text-sm ring-offset-background placeholder:text-slate-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* work description section */}
        <Card>
          <CardHeader>
            <CardTitle>{t('firewall.workDescription')}</CardTitle>
          </CardHeader>
          <CardContent>
            <textarea
              value={formData.work_description || ''}
              onChange={(e) => setFormData(prev => ({ ...prev, work_description: e.target.value }))}
              placeholder={t('firewall.workPlaceholder')}
              disabled={isReadOnly}
              className="flex min-h-[100px] w-full rounded-md border border-input bg-white text-slate-900 px-3 py-2 text-sm ring-offset-background placeholder:text-slate-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            />
          </CardContent>
        </Card>

        {/* security review section */}
        <Card>
          <CardHeader>
            <CardTitle>{t('firewall.securityReview')}</CardTitle>
          </CardHeader>
          <CardContent>
            <textarea
              value={formData.security_review_comment || ''}
              onChange={(e) => setFormData(prev => ({ ...prev, security_review_comment: e.target.value }))}
              placeholder={t('firewall.securityReviewPlaceholder')}
              disabled={isReadOnly}
              className="flex min-h-[100px] w-full rounded-md border border-input bg-white text-slate-900 px-3 py-2 text-sm ring-offset-background placeholder:text-slate-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            />
          </CardContent>
        </Card>

        {/* vendor comment section */}
        <Card>
          <CardHeader>
            <CardTitle>{t('mocDetail.vendorComment')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <label className="flex items-center gap-2 mb-2">
                  <input
                    type="checkbox"
                    checked={formData.needs_vendor_review || false}
                    onChange={(e) => setFormData(prev => ({ ...prev, needs_vendor_review: e.target.checked }))}
                    disabled={isReadOnly}
                    className="w-4 h-4"
                  />
                  <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                    Needs to be reviewed by vendor
                  </span>
                </label>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  Comment for Vendor
                </label>
                <textarea
                  value={formData.vendor_comment || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, vendor_comment: e.target.value }))}
                  placeholder={t('firewall.securityReviewPlaceholder')}
                  disabled={isReadOnly}
                  className="flex min-h-[120px] w-full rounded-md border border-input bg-white dark:bg-slate-800 text-slate-900 dark:text-white px-3 py-2 text-sm ring-offset-background placeholder:text-slate-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* actions */}
        <Card>
          <CardContent className="py-4">
            <div className="flex flex-wrap gap-3 justify-between">
              <Button onClick={() => navigate('/firewall')} variant="outline">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to List
              </Button>
              <div className="flex flex-wrap gap-3">
                <Button onClick={handleSave} disabled={saving || isReadOnly} variant="outline">
                  <Save className="h-4 w-4 mr-2" />
                  Save
                </Button>
                {firewallChangeRequest.status === 'draft' && (
                  <Button onClick={handleSubmitForReview} disabled={saving || isReadOnly} variant="outline">
                    <Send className="h-4 w-4 mr-2" />
                    Send Comment to Vendor
                  </Button>
                )}
                {firewallChangeRequest.status === 'under_review' && (
                  <>
                    <Button onClick={handleApprove} disabled={saving || isReadOnly} className="bg-green-600 hover:bg-green-700 text-white">
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Approve
                    </Button>
                    <Button onClick={handleReject} disabled={saving || isReadOnly} variant="outline" className="text-red-600 hover:text-red-700 border-red-300">
                      <XCircle className="h-4 w-4 mr-2" />
                      Reject
                    </Button>
                  </>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

